/**
 * Internal (platform-operated) executions.
 *
 * Some flows are run by the console itself as part of an operator workflow
 * rather than by a customer testing a network. `Discover Edge Device Location`
 * is the canonical one: the admin Edge Device → Settings tab fires it to get a
 * GNSS fix so it can pre-fill the device's address fields.
 *
 * These runs still write an `executions` doc (they allocate a modem and go
 * through the normal queue), but they are NOT test results. They must not
 * appear in a tenant's dashboard feed, execution list, success-rate tiles or
 * reports — a failed device-setup click is not a failed customer test, and the
 * error text can name infrastructure the tenant has nothing to do with.
 *
 * Two layers keep them out (see issue #267):
 *   1. Attribution — the caller stamps the *device's* owner org (or
 *      `SYSTEM_EXECUTION_ORG_ID` for shared devices), never the org the
 *      operator happened to be viewing at the time.
 *   2. This predicate — applied to every tenant-facing `executions` read, so a
 *      correctly-attributed internal run is still filtered out of test
 *      surfaces. Admin surfaces (/admin/execution-monitor) deliberately skip
 *      the filter and show everything.
 */
/**
 * Sentinel organization id for platform-owned records. Matches the value the
 * system flow/primitive seeds use for `organizationId`. No `organizations/`
 * doc exists under this id, so every `where('organizationId','==',<real org>)`
 * query naturally excludes it.
 */
export const SYSTEM_EXECUTION_ORG_ID = '__system__';
/**
 * `executions.source` values written by operator tooling rather than by a
 * customer running a test. Add new admin-initiated sources here — that is all
 * it takes for every tenant-facing surface to start ignoring them.
 */
export const INTERNAL_EXECUTION_SOURCES = ['admin-discover-location'];
/** True when an `executions.source` value marks a platform-operated run. */
export function isInternalExecutionSource(source) {
    return (typeof source === 'string' &&
        INTERNAL_EXECUTION_SOURCES.includes(source));
}
/**
 * True when an execution doc should be hidden from tenant-facing test
 * surfaces. Checks both the source and the system-org sentinel, so a run
 * stamped `__system__` is filtered even if it were ever read outside an
 * org-scoped query.
 */
export function isInternalExecution(execution) {
    if (!execution)
        return false;
    return (isInternalExecutionSource(execution.source) ||
        execution.organizationId === SYSTEM_EXECUTION_ORG_ID);
}
/**
 * True when an organization id is the platform sentinel rather than a real
 * tenant.
 *
 * This is not just a "skip billing" hint: `__system__` matches Firestore's
 * reserved `__*__` id pattern, so ANY `organizations/__system__` reference
 * throws `INVALID_ARGUMENT` — a read, not only a write. Every org-doc lookup
 * on an execution path must therefore short-circuit on this before touching
 * Firestore. (The submit-time token check didn't, and every super-admin
 * "Discover Location" click on a shared device 500'd with
 * `Resource id "__system__" is invalid because it is reserved`.)
 */
export function isSystemOrgId(orgId) {
    return orgId === SYSTEM_EXECUTION_ORG_ID;
}
